/**
 * @quotech/ui - Navigation Customization Panel
 * 
 * Premium customization UI for:
 * - Hub ordering (drag-drop)
 * - Pinned pages (cross-hub shortcuts)
 * - AI auto-ordering toggle
 * - Quick access shortcuts
 *
 * Sub-components extracted to ./navigation-customization/:
 *   HubsTab.tsx   - Hub reorder drag-and-drop
 *   PinsTab.tsx   - Pinned pages search & management
 *   AITab.tsx     - AI auto-ordering toggle
 */

import { useState, useCallback } from 'react'
import {
  X,
  Check,
  RotateCcw,
  Settings2,
  Layout,
  Pin,
  Sparkles,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { HUB_REGISTRY } from '../lib/hubRegistry'
import { notifyNavCustomizationChanged } from '../hooks/navCustomizationStorage'
import type { HubId, PinnedNavItem, UserCustomization } from './types'
import { HubsTab } from './navigation-customization/HubsTab'
import { PinsTab } from './navigation-customization/PinsTab'
import { AITab } from './navigation-customization/AITab'

export interface NavigationCustomizationProps {
  isOpen: boolean
  onClose: () => void
  currentHub?: HubId
  customization?: Partial<UserCustomization>
  onSave: (customization: Partial<UserCustomization>) => void
  className?: string
}

const STORAGE_KEY = 'quotech_nav_customization'

function getStoredCustomization(): Partial<UserCustomization> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

function saveCustomization(data: Partial<UserCustomization>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    // Tell live useNavCustomization consumers (same tab) the
    // aiAutoOrder flag may have changed so Smart ordering toggles take
    // effect without a reload.
    notifyNavCustomizationChanged()
  } catch {
    // Storage unavailable
  }
}

export function NavigationCustomization({
  isOpen,
  onClose,
  currentHub,
  customization: initialCustomization,
  onSave,
  className,
}: NavigationCustomizationProps) {
  const [activeTab, setActiveTab] = useState<'hubs' | 'pins' | 'ai'>('hubs')
  const [customization, setCustomization] = useState<Partial<UserCustomization>>(() => 
    initialCustomization || getStoredCustomization()
  )
  const [hubOrder, setHubOrder] = useState<HubId[]>(() => {
    const stored = customization.hubOrder
    if (stored?.length) return stored
    return Object.keys(HUB_REGISTRY) as HubId[]
  })
  const [pinnedPages, setPinnedPages] = useState<PinnedNavItem[]>(() => 
    customization.pinnedPages || []
  )
  const [aiEnabled, setAiEnabled] = useState(() => customization.aiAutoOrder ?? true)
  const [searchQuery, setSearchQuery] = useState('')
  const [draggedHub, setDraggedHub] = useState<HubId | null>(null)
  const [dragOverHub, setDragOverHub] = useState<HubId | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const orderedHubs = hubOrder.map(id => HUB_REGISTRY[id]).filter(Boolean)

  const allPages = Object.values(HUB_REGISTRY).flatMap(hub => 
    hub.pages.map(page => ({
      hubId: hub.id,
      hubName: hub.name,
      pageId: page.id,
      pageLabel: page.label,
      href: `${hub.baseUrl}${page.href}`,
      icon: page.icon,
    }))
  )

  const filteredPages = searchQuery
    ? allPages.filter(p => 
        p.pageLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.hubName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  // Hub drag handlers
  const handleHubDragStart = (hubId: HubId) => setDraggedHub(hubId)
  const handleHubDragOver = (hubId: HubId) => {
    if (draggedHub && hubId !== draggedHub) setDragOverHub(hubId)
  }
  const handleHubDrop = (targetHubId: HubId) => {
    if (!draggedHub || draggedHub === targetHubId) {
      setDraggedHub(null); setDragOverHub(null); return
    }
    const newOrder = [...hubOrder]
    const draggedIdx = newOrder.indexOf(draggedHub)
    const targetIdx = newOrder.indexOf(targetHubId)
    newOrder.splice(draggedIdx, 1)
    newOrder.splice(targetIdx, 0, draggedHub)
    setHubOrder(newOrder)
    setHasChanges(true)
    setDraggedHub(null); setDragOverHub(null)
  }
  const handleDragEnd = () => { setDraggedHub(null); setDragOverHub(null) }

  const togglePin = useCallback((page: typeof allPages[0]) => {
    setPinnedPages(prev => {
      const exists = prev.find(p => p.href === page.href)
      if (exists) return prev.filter(p => p.href !== page.href)
      const newPin: PinnedNavItem = {
        id: `${page.hubId}-${page.pageId}`,
        sourceHub: page.hubId,
        label: page.pageLabel,
        href: page.href,
        icon: page.icon,
        order: prev.length,
      }
      return [...prev, newPin]
    })
    setHasChanges(true)
  }, [])

  const removePin = useCallback((href: string) => {
    setPinnedPages(prev => prev.filter(p => p.href !== href))
    setHasChanges(true)
  }, [])

  const handleSave = () => {
    const newCustomization: Partial<UserCustomization> = {
      ...customization, hubOrder, pinnedPages, aiAutoOrder: aiEnabled,
    }
    saveCustomization(newCustomization)
    onSave(newCustomization)
    setHasChanges(false)
    onClose()
  }

  const handleReset = () => {
    setHubOrder(Object.keys(HUB_REGISTRY) as HubId[])
    setPinnedPages([])
    setAiEnabled(true)
    setHasChanges(true)
  }

  if (!isOpen) return null

  const tabs = [
    { id: 'hubs' as const, label: 'Hub Order', icon: Layout },
    { id: 'pins' as const, label: 'Pinned Pages', icon: Pin },
    { id: 'ai' as const, label: 'Smart Features', icon: Sparkles },
  ]

  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', className)} onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Settings2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Customize Navigation</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Personalize your sidebar and quick access</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 dark:border-slate-800">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn(
              'flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === tab.id ? 'border-teal-500 text-teal-600 dark:text-teal-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            )}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'hubs' && (
            <HubsTab orderedHubs={orderedHubs} hubOrder={hubOrder} currentHub={currentHub} draggedHub={draggedHub} dragOverHub={dragOverHub} onDragStart={handleHubDragStart} onDragOver={handleHubDragOver} onDrop={handleHubDrop} onDragEnd={handleDragEnd} />
          )}
          {activeTab === 'pins' && (
            <PinsTab searchQuery={searchQuery} onSearchChange={setSearchQuery} filteredPages={filteredPages} pinnedPages={pinnedPages} onTogglePin={togglePin} onRemovePin={removePin} />
          )}
          {activeTab === 'ai' && (
            <AITab aiEnabled={aiEnabled} onToggle={() => { setAiEnabled(!aiEnabled); setHasChanges(true) }} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
          <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to default
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
            <button onClick={handleSave} disabled={!hasChanges} className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg',
              hasChanges ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
            )}>
              <Check className="w-3.5 h-3.5" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NavigationCustomization
